import { ChevronDown } from "lucide-react";
import { useState } from "react";
import i18n from "../../../i18n";

function sq(key, options) {
  return i18n.t(`salesQuotation.${key}`, options);
}

function mobileInputClass(readonly = false) {
  return `min-h-[44px] w-full rounded-[12px] border border-[#d1d9e8] bg-white px-3 text-[13px] font-semibold text-[#2d394c] outline-hidden focus:border-[#95addd] ${
    readonly ? "bg-[#f8fbff] text-[#6f82a1]" : ""
  }`;
}

function Card({ children, className = "" }) {
  return (
    <div className={`rounded-[14px] border border-[#e1e7f2] bg-white p-3 ${className}`}>{children}</div>
  );
}

function Row({ label, value, mute = false, strong = false, valueClassName = "" }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className={`text-[13px] ${mute ? "text-[#8ea0bd]" : "text-[#51657f]"}`}>{label}</span>
      <span
        className={`text-right text-[13px] ${
          strong ? "font-semibold text-[#23324c]" : "font-semibold text-[#23324c]"
        } ${valueClassName}`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Mobile Pricing (Cost + Pricing) — HTML-like card flow, full app fields.
 * Calculations / PMC patching stay in CostPricingTab.
 */
export function QuotationCostPricingMobile({
  editable,
  formatMoney,
  formatMoneyWhole,
  localizedFieldProps,
  // Hero / realized
  calculatedSellingPrice,
  realizedSellingTotal,
  difference,
  derivedMultiplier,
  realizedMarginPercent,
  showMarginWarning,
  targetMargin,
  // By system
  systemBreakdown = [],
  // Inputs
  overheadsPercent,
  setOverheadsPercent,
  markUpPercent,
  setMarkUpPercent,
  // Cost / profit summary
  totalCost,
  overheadsAmount,
  netMarginAmount,
  netProfitPercent,
  grossMarginAmount,
  grossProfitPercent,
  // Cost details breakdown
  hasPmcConfigs,
  costRows = [],
  // PMC
  pmcGroups = [],
  onUpdatePmcEntry,
}) {
  const [openPmcIds, setOpenPmcIds] = useState(() => new Set());
  const [costDetailsOpen, setCostDetailsOpen] = useState(false);

  function togglePmc(id) {
    setOpenPmcIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function renderPmcField(row, column) {
    const rawValue =
      column.key === "unitRate"
        ? row.unitRate ?? ""
        : column.key === "rate"
          ? row.rate ?? row.ctc ?? ""
          : column.key === "totalCost"
            ? row.totalCost ?? row.amount ?? ""
            : (row[column.key] ?? "");
    const value = rawValue === 0 ? "0" : String(rawValue ?? "");

    if (column.key === "description") {
      return (
        <p className="text-[13px] font-semibold text-[#23324c]">
          {row.lineName || row.description || "-"}
        </p>
      );
    }

    if (column.key === "totalCost" && (column.computed || column.readonly)) {
      return (
        <p className="text-right text-[13px] font-semibold text-[#23324c]">
          {formatMoney(row.totalCost)}
        </p>
      );
    }

    if (!editable || column.readonly || !onUpdatePmcEntry) {
      return (
        <p className={`text-[13px] text-[#2d394c] ${column.align === "left" ? "text-left" : "text-right"}`}>
          {column.key === "totalCost" ? formatMoney(row.totalCost) : value || "-"}
        </p>
      );
    }

    return (
      <input
        value={value}
        onChange={(event) => onUpdatePmcEntry(row.id, column.key, event.target.value)}
        disabled={!editable}
        inputMode="decimal"
        {...(localizedFieldProps?.() || {})}
        className={mobileInputClass(!editable)}
      />
    );
  }

  const marginTone =
    showMarginWarning || realizedMarginPercent < 10
      ? "bg-[#fff3cd] text-[#d47700]"
      : "bg-[#e8f6ee] text-[#2b7a4b]";

  return (
    <div className="space-y-2.5">
      {/* HTML hero: Calculated selling + item total + difference */}
      <Card>
        <p className="text-[12px] font-medium text-[#8ea0bd]">{sq("mobileCalculatedSelling")}</p>
        <p className="mt-1 text-[26px] font-extrabold tracking-[-0.03em] text-[#23324c]">
          {formatMoney(calculatedSellingPrice)}
        </p>
        <div className="mt-2 space-y-0.5 border-t border-[#eef1f7] pt-2">
          <Row label={sq("mobileYourItemTotal")} value={formatMoney(realizedSellingTotal)} mute />
          <Row
            label={sq("mobilePricingDifference")}
            value={formatMoney(difference)}
            mute
          />
          <Row
            label={sq("mobileDerivedMultiplier")}
            value={derivedMultiplier > 0 ? `${derivedMultiplier.toFixed(2)}x` : "—"}
            mute
          />
        </div>
      </Card>

      {/* By system */}
      {systemBreakdown.length > 0 ? (
        <Card>
          <p className="mb-1.5 text-[13px] font-bold text-[#23324c]">{sq("mobileBySystem")}</p>
          <div className="space-y-0.5">
            {systemBreakdown.map((item) => (
              <Row key={item.name} label={item.name} value={formatMoney(item.amount)} mute />
            ))}
          </div>
        </Card>
      ) : null}

      {/* Overheads % · Mark up % — HTML g2 */}
      <div className="grid grid-cols-2 gap-2.5">
        <label className="block text-[12px] font-semibold text-[#8ea0bd]">
          {sq("mobileOverheadsPct")}
          <input
            value={overheadsPercent}
            onChange={(event) => setOverheadsPercent?.(event.target.value)}
            disabled={!editable}
            inputMode="decimal"
            maxLength={5}
            {...(localizedFieldProps?.() || {})}
            className={`mt-1 ${mobileInputClass(!editable)}`}
          />
        </label>
        <label className="block text-[12px] font-semibold text-[#8ea0bd]">
          {sq("mobileMarkUpPct")}
          <input
            value={markUpPercent}
            onChange={(event) => setMarkUpPercent?.(event.target.value)}
            disabled={!editable}
            inputMode="decimal"
            maxLength={5}
            {...(localizedFieldProps?.() || {})}
            className={`mt-1 ${mobileInputClass(!editable)}`}
          />
        </label>
      </div>

      {/* Total cost · overheads · profit · realized margin */}
      <Card>
        <Row label={sq("mobileTotalCost")} value={formatMoney(totalCost)} strong />
        <Row label={sq("mobileOverheadsAmount")} value={formatMoneyWhole(overheadsAmount)} mute />
        <Row
          label={`${sq("mobileNetProfit")} (${netProfitPercent.toFixed(1)}%)`}
          value={formatMoneyWhole(netMarginAmount)}
        />
        <Row
          label={`${sq("mobileGrossProfit")} (${grossProfitPercent.toFixed(1)}%)`}
          value={formatMoneyWhole(grossMarginAmount)}
        />
        <div className="mt-1.5 flex items-center justify-between gap-2 border-t border-[#eef1f7] pt-2">
          <span className="text-[13px] text-[#51657f]">{sq("mobileRealizedMargin")}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-bold ${marginTone}`}>
            {realizedMarginPercent.toFixed(1)}%
          </span>
        </div>
        {showMarginWarning ? (
          <p className="mt-2 rounded-[10px] bg-[#fff3cd] px-3 py-2 text-[12px] leading-5 text-[#d47700]">
            {sq("mobileMarginBelowTarget", { target: targetMargin })}
          </p>
        ) : null}
      </Card>

      {/* App: Supply / Install / PMC cost breakdown (collapsible) */}
      <Card className="!p-0 overflow-hidden">
        <button
          type="button"
          onClick={() => setCostDetailsOpen((open) => !open)}
          className="flex w-full items-center justify-between gap-2 px-3 py-3 text-left"
        >
          <div>
            <p className="text-[13px] font-bold text-[#23324c]">{sq("mobileCostDetails")}</p>
            <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#e28a00]">
              {sq("internal")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-semibold text-[#8ea0bd]">{formatMoney(totalCost)}</span>
            <ChevronDown
              className={`h-4 w-4 text-[#8ea0bd] transition ${costDetailsOpen ? "rotate-180" : ""}`}
            />
          </div>
        </button>
        {costDetailsOpen ? (
          <div className="space-y-0.5 border-t border-[#eef1f7] px-3 py-2.5">
            {costRows.map((row) => (
              <Row
                key={row.head}
                label={`${row.head}${row.percent ? ` · ${row.percent}` : ""}`}
                value={row.amount}
                mute={!row.emphasis && !row.grand}
                strong={Boolean(row.grand || row.emphasis)}
                valueClassName={row.grand ? "text-[#2944a8]" : ""}
              />
            ))}
          </div>
        ) : null}
      </Card>

      {/* PMC heads — HTML details style; all app types incl. LUMPSUM */}
      {hasPmcConfigs && pmcGroups.length > 0
        ? pmcGroups.map((head) => {
            const open = openPmcIds.has(head.id);
            const hint =
              String(head.costHeadType || "").toUpperCase().includes("MANPOWER")
                ? sq("mobilePmcManpowerHint")
                : String(head.costHeadType || "").toUpperCase().includes("BOQ")
                  ? sq("mobilePmcBoqHint")
                  : String(head.costHeadType || "").toUpperCase().includes("LUMP")
                    ? sq("mobilePmcLumpsumHint")
                    : null;
            return (
              <Card key={head.id} className="!p-0 overflow-hidden">
                <button
                  type="button"
                  onClick={() => togglePmc(head.id)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-3 text-left"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-bold text-[#23324c]">{head.title}</p>
                    {hint ? <p className="mt-0.5 text-[11px] text-[#8ea0bd]">{hint}</p> : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-[13px] font-semibold text-[#8ea0bd]">
                      {formatMoney(head.totalCost)}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-[#8ea0bd] transition ${open ? "rotate-180" : ""}`}
                    />
                  </div>
                </button>
                {open ? (
                  <div className="space-y-3 border-t border-[#eef1f7] bg-[#fafbfe] px-3 py-3">
                    {head.rows?.length ? (
                      head.rows.map((row, index) => (
                        <div
                          key={`${head.id}-${row.id || index}`}
                          className="space-y-2 rounded-[12px] border border-[#e4eaf4] bg-white p-3"
                        >
                          {renderPmcField(row, { key: "description", label: "Description" })}
                          <div className="grid grid-cols-2 gap-2.5">
                            {(head.columns || [])
                              .filter((column) => column.key !== "description")
                              .map((column) => (
                                <div
                                  key={`${head.id}-${row.id || index}-${column.reactKey || column.id || column.key}`}
                                  className={
                                    column.key === "totalCost" &&
                                    String(head.costHeadType || "").toUpperCase().includes("LUMP")
                                      ? "col-span-2"
                                      : ""
                                  }
                                >
                                  <p className="mb-1 text-[11px] font-semibold text-[#8ea0bd]">
                                    {column.label}
                                  </p>
                                  {renderPmcField(row, column)}
                                </div>
                              ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-[12px] text-[#8ea0bd]">No active line items for this cost head.</p>
                    )}
                  </div>
                ) : null}
              </Card>
            );
          })
        : null}
    </div>
  );
}
